import React, { useEffect, useState } from "react";
import usersApi from "../services/users/UserApi";
import { Flowbite, Modal } from "flowbite-react";
import CommonHeader from "../components/CommonHeader";
import ToastNotification, { showToast } from "../components/Toaster";
import PillButton from "../components/button/Pills";

const SubscriptionsPage: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<any | null>(
    null
  );
  const [newEndDate, setNewEndDate] = useState<string>("");

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await usersApi.getSubscriptions();
      if (response.success) {
        setSubscriptions(response.value.data.contents);
      } else {
        setError(response.error.message || "Failed to fetch subscriptions.");
      }
    } catch (err) {
      setError("Failed to fetch subscriptions.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const handleUpdateSubscription = async () => {
    if (selectedSubscription && newEndDate) {
      try {
        const endDate = new Date(newEndDate).getTime();
        const response = await usersApi.updateSubscription({
          userId: selectedSubscription.user.id,
          endDate,
        });
        if (response.success) {
          fetchSubscriptions();
          showToast("Subscription updated successfully!", "success");
          setSubscriptions((prev) =>
            prev.map((sub) =>
              sub.user.id === selectedSubscription.user.id
                ? { ...sub, endDate }
                : sub
            )
          );
          setOpenUpdateModal(false);
        } else {
          showToast("Failed to update subscription.", "error");
        }
      } catch (err) {
        showToast("Failed to update subscription.", "error");
      }
    }
  };

  return (
    <Flowbite>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="Subscriptions" />
          {loading ? (
            <p className="text-gray-500 text-center dark:text-gray-50 mt-[200px]">
              Loading...
            </p>
          ) : error ? (
            <p className="text-red-500 text-center">{error}</p>
          ) : subscriptions && subscriptions.length === 0 ? (
            <p className="text-gray-500 text-center dark:text-gray-300">No records found.</p>
          ) : (
            <div className="w-full p-16">
              <div className="relative overflow-x-auto shadow-md border border-ez-border bg-ez-surface">
                <table className="w-full text-sm text-left rtl:text-right text-ez-text-secondary dark:text-ez-text-primary">
                  <thead className="text-xs uppercase bg-ez-elevated dark:bg-ez-elevated dark:text-ez-text-secondary text-ez-text-secondary">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Username
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Plan Type
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Start Date
                      </th>
                      <th scope="col" className="px-6 py-3">
                        End Date
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Remaining Days
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-b border-ez-border odd:bg-ez-surface even:bg-ez-elevated hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
                        <td className="px-6 py-4 text-ez-text-primary">
                          {sub.user.username}
                        </td>
                        <td className="px-6 py-4 text-ez-text-primary">
                          {sub.planType}
                        </td>
                        <td className="px-6 py-4 text-ez-text-primary">
                          {new Date(sub.startDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-ez-text-primary">
                          {new Date(sub.endDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-ez-text-primary text-left">
                          {sub.remainingDays}
                        </td>
                        <td className="px-6 py-4 dark:text-gray-50">
                          <button
                            className="action-btn"
                            onClick={() => {
                              setSelectedSubscription(sub);
                              setNewEndDate(
                                new Date(sub.endDate)
                                  .toISOString()
                                  .split("T")[0]
                              );
                              setOpenUpdateModal(true);
                            }}
                          >
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              height="15px"
                              width="15px"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={openUpdateModal}
        size="md"
        onClose={() => setOpenUpdateModal(false)}
        popup
      >
        <Modal.Header className="bg-ez-elevated rounded-t-lg">
          <h3 className="text-lg font-bold text-ez-text-primary">Update Subscription</h3>
        </Modal.Header>
        <Modal.Body className="bg-ez-surface">
          <div className="space-y-4 ">
            <p className="text-ez-text-secondary">
              Update subscription for:{" "}
              <strong>{selectedSubscription?.user.username}</strong>
            </p>
            <input
              type="date"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
              min={
                selectedSubscription?.startDate
                  ? new Date(selectedSubscription.startDate).toISOString().split("T")[0]
                  : undefined
              }
              className="w-full p-2 border border-ez-border rounded bg-ez-elevated text-ez-text-primary"
            />
            <div className="flex justify-end gap-3">
              <PillButton variant="primary" size="md" onClick={handleUpdateSubscription}>
                Save
              </PillButton>
              <PillButton variant="secondary" onClick={() => setOpenUpdateModal(false)}>
                Cancel
              </PillButton>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Flowbite>
  );
};

export default SubscriptionsPage;



